import { safeStorage } from './storage';

/** Keeps a browser's drawings apart in local development, such as one per browser test. */
const OWNER_KEY = 'draw:owner';

/** Browsers refuse a request kept alive past the page when its body is larger than this. */
const KEEPALIVE_LIMIT = 64 * 1024;

/** Makes the sign-in current before a request, such as `AuthService.renew`. */
export interface SignIn {
  renew(): Promise<void>;
  /** The server refused the sign-in, so it has to be made again. */
  expired(): void;
}

/** JSON requests to our own API, as the signed-in person. */
export class ApiClient {
  constructor(
    private readonly storage: Storage | null = safeStorage(),
    private readonly signIn: SignIn | null = null,
  ) {}

  async send(url: string, method = 'GET', body?: unknown, leaving = false): Promise<Response> {
    // An expired token would be refused, so it is renewed first; a page going away cannot wait.
    if (!leaving) await this.signIn?.renew();
    const headers: Record<string, string> = {};
    // The signed-in person travels in Netlify Identity's cookie, which the browser sends itself.
    const owner = this.storage?.getItem(OWNER_KEY);
    if (owner) headers['x-draw-owner'] = owner;
    if (body !== undefined) headers['content-type'] = 'application/json';
    const text = body === undefined ? undefined : JSON.stringify(body);
    const response = await fetch(url, {
      method,
      headers,
      ...(text === undefined ? {} : { body: text }),
      keepalive: leaving && (text?.length ?? 0) < KEEPALIVE_LIMIT,
    });
    // Renewing could not save the sign-in, so no retry will either.
    if (response.status === 401) this.signIn?.expired();
    return response;
  }

  /** The answer's JSON; a failed answer is an error. */
  async read(response: Response): Promise<unknown> {
    if (!response.ok) throw new Error(`The server answered ${response.status}`);
    return response.status === 204 ? null : response.json();
  }
}
